/**
 * The import pattern's words (#28, v2): the page a list with `import` opens, and every verdict the
 * SDK's reader gives a file (`import.problem.*`) or its apply refuses with (`import.refusal.*`).
 * Kept beside the catalogue and spread into it, so a parallel change to the catalogue does not
 * collide with these.
 */
export const IMPORTS_EN = {
    'list.import': 'Import a file',
    'list.import.lede': 'Map the file’s columns to fields, test it, then import. Nothing is written until the whole file is valid.',
    'list.import.template.label': 'Template',
    'list.import.template.none': 'No template',
    'list.import.template.shared': '{name} · shared',
    'list.import.save': 'Save as template',
    'list.import.upload': 'Upload file',
    'list.import.test': 'Test',
    'list.import.apply': 'Import',
    'list.import.cancel': 'Cancel',
    'list.import.reading': 'Reading the file…',
    'list.import.empty.title': 'Upload an Excel or CSV file to fill «{list}»',
    'list.import.empty.hint': 'Or drop the file here. The first row holds the column names; they need not match the system’s.',
    'list.import.downloads': 'Ready-made files:',
    'list.import.download.template': 'The import template',
    'list.import.download.current': 'The current rows, to edit',
    'list.import.file.size': '{rows:rows} · {columns:columns}',
    'list.import.options': 'File options',
    'list.import.options.file': 'File',
    'list.import.options.format': 'Format',
    'list.import.options.rows': 'Rows',
    'list.import.option.sheet': 'Sheet',
    'list.import.option.header_row': 'Header row',
    'list.import.option.start_row': 'Start at row',
    'list.import.option.date_format': 'Date format',
    'list.import.option.decimal': 'Decimal separator',
    'list.import.option.thousands': 'Thousands separator',
    'list.import.option.upsert': 'Update the records already here, found by «{key}»',
    'list.import.sep.dot': 'Dot .',
    'list.import.sep.comma': 'Comma ,',
    'list.import.sep.space': 'Space',
    'list.import.sep.apostrophe': 'Apostrophe \'',
    'list.import.sep.none': 'None',
    'list.import.head.file': 'File column',
    'list.import.head.field': 'Field',
    'list.import.head.comment': 'Comment',
    'list.import.field.none': 'Not imported',
    'list.import.field.search': 'Search for a field…',
    'list.import.field.nothing': 'No field matches',
    'list.import.comment.unmapped': 'Not mapped',
    'list.import.required': 'required',
    'list.import.type.text': 'Text',
    'list.import.type.date': 'A day',
    'list.import.type.integer': 'A whole number',
    'list.import.type.number': 'A number',
    'list.import.type.boolean': 'Yes or no',
    'list.import.type.choice': 'One of: {values}',
    'list.import.type.ref': 'Found by {lookup}',
    'list.import.constants.title': 'Values for every row',
    'list.import.constants.lede': 'A required field no column carries takes one value for every row.',
    'list.import.constant.choose': 'Choose a value',
    'list.import.suggested.headers': '{mapped} of {total} columns were matched by their names. Press «Test» to check the file without writing anything.',
    'list.import.suggested.template': 'The template «{name}» was applied because the file’s headers match it.',
    'list.import.suggested.last': 'The settings you last imported a file with the same headers were applied.',
    'list.import.template.applied': 'The template «{name}» was applied.',
    'list.import.template.fresh': 'New in this file and not in the template: {columns}. Map or ignore them, then update the template if you want.',
    'list.import.template.missing': 'Expected by the template and missing from this file: {columns}.',
    'list.import.template.update': 'Update the template',
    'list.import.template.updated': 'The template «{name}» was updated.',
    'list.import.valid': 'Everything is valid: {create:rows} to create · {update:rows} to update.',
    'list.import.valid.create': 'Everything is valid: {create:rows} to create.',
    'list.import.valid.hint': 'Nothing has been written yet. «Import» writes every row.',
    'list.import.refused': 'Nothing was imported. {n:problems} in {rows:rows}:',
    'list.import.didYouMean': 'Did you mean «{value}»?',
    'list.import.translate': 'Use «{value}»',
    'list.import.download.errors': 'Download the file with an error column',
    'list.import.retest': 'The settings changed since the test: test again before importing.',
    'list.import.lines.one': 'Line {line}',
    'list.import.lines.many': 'Lines {lines}',
    'list.import.lines.more': 'Lines {lines} and {n} more',
    'list.import.wholeFile': 'The whole file',
    'list.import.errorColumn': 'Error',
    'list.import.failed': 'The file could not be sent: {detail}',
    'list.import.importer.failed': 'The import could not be read, so no file can be mapped.',
    'list.import.save.title': 'Save this import as a template',
    'list.import.save.name': 'Template name',
    'list.import.save.description': 'Description',
    'list.import.save.who': 'Who uses it',
    'list.import.save.me': 'Only me',
    'list.import.save.everyone': 'Everyone who may import into this list',
    'list.import.save.what': 'What the template keeps',
    'list.import.save.never': 'Neither the file nor any of its rows is saved: a template is settings only.',
    'list.import.save.ok': 'Save the template',
    'list.import.saved': 'The template «{name}» was saved.',
    'list.import.summary.mapping': 'The mapping of {n:columns}',
    'list.import.summary.mappingIgnoring': 'The mapping of {n:columns}, ignoring {columns}',
    'list.import.summary.options': 'The sheet «{sheet}», the date format {date}, starting at row {row}',
    'list.import.summary.constant': 'A fixed value: «{field}» = {value}',
    'list.import.summary.translation': 'A value translated: «{from}» → {to}',
    'list.import.summary.mode.upsert': 'The way: add the new and update those already here by «{key}»',
    'list.import.summary.mode.create': 'The way: add every row as new',
    'list.import.filter': 'The {rows} rows imported from «{file}».',
    'list.import.filter.unnamed': 'The rows of one file import.',
    'list.import.filter.clear': 'Show every row',
    'import.problem.ref_several': '{count} records are «{value}» in «{header}»: name it more exactly.',
    'import.problem.column_unmapped': 'Nothing fills «{header}»: choose a column or a value for every row.',
    'import.problem.key_unmapped': 'An update finds its record by «{header}»: map a file column to it.',
    'import.problem.key_twice': 'Line {first} already creates or updates this «{header}».',
    'import.problem.mapped_twice': 'Two file columns fill «{header}».',
    'import.problem.mapping_unknown': 'There is no field «{field}».',
    'import.problem.lookup_unknown': '«{header}» is not found by «{lookup}».',
    'import.problem.sheet': 'The file has no sheet «{sheet}».',
    'import.problem.mode': 'This list does not import that way.',
    'import.problem.date_format': '«{format}» is not a date format.',
    'import.problem.settings': 'The import options are not valid: choose them again.',
    'import.refusal.not_tested': 'Test the file before importing it.',
    'import.refusal.no_file': 'This upload was read by an earlier version: upload the file again.',
    'import.refusal.template_name': 'A template is named in 1 to 120 characters.',
    'import.refusal.template_name_taken': 'You already have a template named «{name}».',
    'import.refusal.template_description': 'A description is 1,000 characters at most.',
    'import.refusal.template_headers': 'A template is saved from a tested file, with its headers.',
    'import.refusal.template_not_found': 'There is no such template.',
    'import.refusal.template_forbidden': 'Only its author, or whoever may import into this list, changes a shared template.',
    'import.problem.ref_none': 'No «{header}» is called «{value}».',
    'change.cause.import': 'from the import of {file}',
    'change.cause.import.unnamed': 'from a file import',
    'import.problem.header_missing': 'The file has no column «{header}».',
    'import.problem.header_unknown': 'The column «{header}» is not in the template.',
    'import.problem.header_twice': 'The column «{header}» appears twice.',
    'import.problem.required': '«{header}» is empty.',
    'import.problem.integer': '«{header}» is not a whole number: {value}',
    'import.problem.number': '«{header}» is not a number: {value}',
    'import.problem.date': '«{header}» is not a day written {format}: {value}',
    'import.problem.date_ambiguous': '«{header}» could be read day first or month first: {value}. Write it as 2021-04-03, or choose the date format.',
    'import.problem.date_unread': '«{header}» is not a day: {value}',
    'list.import.date.auto': 'Detect automatically',
    'list.import.guide.sheet': 'Instructions',
    'list.import.guide.lede': 'Fill the first sheet, one row per record under its header row; leave the headers as they are. This sheet is not imported.',
    'list.import.guide.column': 'Column',
    'list.import.guide.required': 'Required',
    'list.import.guide.what': 'What to write',
    'list.import.guide.values': 'Accepted values',
    'list.import.guide.example': 'Example',
    'list.import.guide.requiredPrompt': 'Required',
    'list.import.create': 'Add «{value}»',
    'list.import.created': '«{value}» was added. The file is tested again.',
    'unit.rows.zero': '{n} rows',
    'unit.rows.one': '{n} row',
    'unit.rows.two': '{n} rows',
    'unit.rows.few': '{n} rows',
    'unit.rows.many': '{n} rows',
    'unit.rows.other': '{n} rows',
    'unit.rows_in.zero': '{n} rows',
    'unit.rows_in.one': '{n} row',
    'unit.rows_in.two': '{n} rows',
    'unit.rows_in.few': '{n} rows',
    'unit.rows_in.many': '{n} rows',
    'unit.rows_in.other': '{n} rows',
    'unit.columns.zero': '{n} columns',
    'unit.columns.one': '{n} column',
    'unit.columns.two': '{n} columns',
    'unit.columns.few': '{n} columns',
    'unit.columns.many': '{n} columns',
    'unit.columns.other': '{n} columns',
    'unit.columns_of.zero': '{n} columns',
    'unit.columns_of.one': '{n} column',
    'unit.columns_of.two': '{n} columns',
    'unit.columns_of.few': '{n} columns',
    'unit.columns_of.many': '{n} columns',
    'unit.columns_of.other': '{n} columns',
    'unit.problems.zero': '{n} problems',
    'unit.problems.one': '{n} problem',
    'unit.problems.two': '{n} problems',
    'unit.problems.few': '{n} problems',
    'unit.problems.many': '{n} problems',
    'unit.problems.other': '{n} problems',
    'import.problem.boolean': '«{header}» is not yes or no: {value}',
    'import.problem.choice': '«{header}» is not one of: {choices}',
    'import.problem.too_long': '«{header}» is longer than {max} characters.',
    'import.problem.duplicate': 'Line {first} already has this «{header}».',
    'import.problem.no_rows': 'The file has no rows under its header.',
    'import.problem.too_many': 'A file carries at most {max:rows}.',
    'import.problem.no_file': 'No file was sent.',
    'import.problem.empty': 'The file is empty.',
    'import.problem.too_large': 'The file is too large.',
    'import.problem.format': 'The file is read as .xlsx or .csv: save it as one of those and upload that.',
    'import.problem.already_applied': 'This file was applied on {at}: applying it again would write its rows twice.',
    'import.refusal.not_found': 'There is no such import.',
    'import.refusal.applied': 'This import was applied already.',
    'import.refusal.has_errors': 'A file with refused rows applies nothing. Check it again to read why.',
    'change.field.occasion.import_id': 'From file import',
    'change.field.import_template.importer': 'Importer',
    'change.field.import_template.name': 'Name',
    'change.field.import_template.description': 'Description',
    'change.field.import_template.shared': 'Shared',
    'change.field.import_template.settings': 'Settings',
    'change.field.import_template.headers': 'File headers',
    'change.field.import_template.created_by': 'Created by',
};
export const IMPORTS_AR = {
    'list.import': 'استيراد ملف',
    'list.import.lede': 'طابق أعمدة الملف بالحقول، واختبره، ثم استورده. لا يُكتب شيء حتى يصحّ الملف كله.',
    'list.import.template.label': 'القالب',
    'list.import.template.none': 'بدون قالب',
    'list.import.template.shared': '{name} · مشترك',
    'list.import.save': 'حفظ كقالب',
    'list.import.upload': 'تحميل ملف',
    'list.import.test': 'اختبار',
    'list.import.apply': 'استيراد',
    'list.import.cancel': 'إلغاء',
    'list.import.reading': 'يُقرأ الملف…',
    'list.import.empty.title': 'ارفع ملف Excel أو CSV لتملأ «{list}»',
    'list.import.empty.hint': 'أو اسحب الملف إلى هنا. الصف الأول عناوين الأعمدة، ولا يلزم أن تطابق أسماء النظام.',
    'list.import.downloads': 'ملفات جاهزة:',
    'list.import.download.template': 'قالب الاستيراد',
    'list.import.download.current': 'الصفوف الحالية للتعديل',
    'list.import.file.size': '{rows:rows} · {columns:columns}',
    'list.import.options': 'خيارات الملف',
    'list.import.options.file': 'الملف',
    'list.import.options.format': 'التنسيق',
    'list.import.options.rows': 'الصفوف',
    'list.import.option.sheet': 'الورقة',
    'list.import.option.header_row': 'صف العناوين',
    'list.import.option.start_row': 'ابدأ من الصف',
    'list.import.option.date_format': 'صيغة التاريخ',
    'list.import.option.decimal': 'الفاصل العشري',
    'list.import.option.thousands': 'فاصل الآلاف',
    'list.import.option.upsert': 'حدّث السجلات الموجودة، ويُعرف السجل بـ«{key}»',
    'list.import.sep.dot': 'نقطة .',
    'list.import.sep.comma': 'فاصلة ,',
    'list.import.sep.space': 'مسافة',
    'list.import.sep.apostrophe': 'فاصلة عليا \'',
    'list.import.sep.none': 'بلا فاصل',
    'list.import.head.file': 'عمود الملف',
    'list.import.head.field': 'الحقل في النظام',
    'list.import.head.comment': 'ملاحظات',
    'list.import.field.none': 'لا يُستورد',
    'list.import.field.search': 'ابحث عن حقل…',
    'list.import.field.nothing': 'لا حقل يطابق',
    'list.import.comment.unmapped': 'لم يُطابَق',
    'list.import.required': 'مطلوب',
    'list.import.type.text': 'نص',
    'list.import.type.date': 'تاريخ',
    'list.import.type.integer': 'عدد صحيح',
    'list.import.type.number': 'رقم',
    'list.import.type.boolean': 'نعم أو لا',
    'list.import.type.choice': 'واحد من: {values}',
    'list.import.type.ref': 'يُبحث عنه بـ{lookup}',
    'list.import.constants.title': 'قيم لكل الصفوف',
    'list.import.constants.lede': 'الحقل المطلوب الذي لا يقابله عمود يأخذ قيمة واحدة لكل الصفوف.',
    'list.import.constant.choose': 'اختر قيمة',
    'list.import.suggested.headers': 'طوبق {mapped} من {total} أعمدة بأسمائها. اضغط «اختبار» ليُفحص الملف دون أن يُكتب شيء.',
    'list.import.suggested.template': 'طُبّق القالب «{name}» لأن عناوين الملف تطابقه.',
    'list.import.suggested.last': 'طُبّقت آخر إعدادات استوردت بها ملفًا بالعناوين نفسها.',
    'list.import.template.applied': 'طُبّق القالب «{name}».',
    'list.import.template.fresh': 'في الملف أعمدة جديدة ليست في القالب: {columns}. طابقها أو تجاهلها، ثم «حدّث القالب» إن أردت.',
    'list.import.template.missing': 'أعمدة يتوقعها القالب وليست في الملف: {columns}.',
    'list.import.template.update': 'حدّث القالب',
    'list.import.template.updated': 'حُدّث القالب «{name}».',
    'list.import.valid': 'كل شيء صحيح: {create:rows} للإضافة · {update:rows} للتحديث.',
    'list.import.valid.create': 'كل شيء صحيح: {create:rows} للإضافة.',
    'list.import.valid.hint': 'لم يُكتب شيء بعد. «استيراد» يكتب الصفوف كلها.',
    'list.import.refused': 'لم يُستورد شيء. وجدنا {n:problems} في {rows:rows_in}:',
    'list.import.didYouMean': 'هل تقصد «{value}»؟',
    'list.import.translate': 'استعمل «{value}»',
    'list.import.download.errors': 'تنزيل الملف بعمود الأخطاء',
    'list.import.retest': 'تغيّرت الإعدادات بعد الاختبار: اختبر من جديد قبل الاستيراد.',
    'list.import.lines.one': 'الصف {line}',
    'list.import.lines.many': 'الصفوف {lines}',
    'list.import.lines.more': 'الصفوف {lines} و{n} غيرها',
    'list.import.wholeFile': 'الملف كله',
    'list.import.errorColumn': 'الخطأ',
    'list.import.failed': 'تعذّر إرسال الملف: {detail}',
    'list.import.importer.failed': 'تعذّرت قراءة إعداد الاستيراد، فلا يُطابَق ملف عليه.',
    'list.import.save.title': 'حفظ عملية الاستيراد كقالب',
    'list.import.save.name': 'اسم القالب',
    'list.import.save.description': 'الوصف',
    'list.import.save.who': 'من يستعمله',
    'list.import.save.me': 'أنا وحدي',
    'list.import.save.everyone': 'كل من يملك صلاحية الاستيراد إلى هذه القائمة',
    'list.import.save.what': 'ما يُحفظ في القالب',
    'list.import.save.never': 'لا يُحفظ الملف ولا أي صف من بياناته. القالب إعدادات فقط.',
    'list.import.save.ok': 'حفظ القالب',
    'list.import.saved': 'حُفظ القالب «{name}».',
    'list.import.summary.mapping': 'مطابقة {n:columns_of}',
    'list.import.summary.mappingIgnoring': 'مطابقة {n:columns_of}، وتجاهل {columns}',
    'list.import.summary.options': 'الورقة «{sheet}»، وصيغة التاريخ {date}، والبدء من الصف {row}',
    'list.import.summary.constant': 'قيمة ثابتة: «{field}» = {value}',
    'list.import.summary.translation': 'تحويل قيم: «{from}» ← {to}',
    'list.import.summary.mode.upsert': 'الطريقة: إضافة الجديد وتحديث الموجود بـ«{key}»',
    'list.import.summary.mode.create': 'الطريقة: إضافة كل الصفوف جديدة',
    'list.import.filter': 'الصفوف المستوردة من «{file}»: {rows}.',
    'list.import.filter.unnamed': 'صفوف استيراد ملف واحد.',
    'list.import.filter.clear': 'اعرض كل الصفوف',
    'import.problem.ref_several': '{count} سجلات بالقيمة «{value}» في «{header}»: حدّده بدقة أكبر.',
    'import.problem.column_unmapped': 'لا شيء يملأ «{header}»: اختر عمودًا أو قيمة لكل الصفوف.',
    'import.problem.key_unmapped': 'التحديث يجد السجل بـ«{header}»: طابق به عمودًا من الملف.',
    'import.problem.key_twice': 'السطر {first} ينشئ أو يحدّث «{header}» نفسه.',
    'import.problem.mapped_twice': 'عمودان من الملف يملآن «{header}».',
    'import.problem.mapping_unknown': 'لا يوجد حقل «{field}».',
    'import.problem.lookup_unknown': 'لا يُبحث عن «{header}» بـ«{lookup}».',
    'import.problem.sheet': 'لا توجد في الملف ورقة «{sheet}».',
    'import.problem.mode': 'لا تستورد هذه القائمة بهذه الطريقة.',
    'import.problem.date_format': '«{format}» ليست صيغة تاريخ.',
    'import.problem.settings': 'خيارات الاستيراد غير صحيحة: اخترها من جديد.',
    'import.refusal.not_tested': 'اختبر الملف قبل استيراده.',
    'import.refusal.no_file': 'قُرئ هذا الرفع بإصدار سابق: ارفع الملف من جديد.',
    'import.refusal.template_name': 'اسم القالب من حرف إلى 120 حرفًا.',
    'import.refusal.template_name_taken': 'لديك قالب باسم «{name}» من قبل.',
    'import.refusal.template_description': 'الوصف 1000 حرف على الأكثر.',
    'import.refusal.template_headers': 'يُحفظ القالب من ملف مُختبَر، بعناوينه.',
    'import.refusal.template_not_found': 'لا يوجد قالب كهذا.',
    'import.refusal.template_forbidden': 'لا يغيّر القالب المشترك إلا صاحبه أو من يملك صلاحية الاستيراد إلى هذه القائمة.',
    'import.problem.ref_none': 'لا يوجد «{header}» بالقيمة «{value}».',
    'change.cause.import': 'من استيراد {file}',
    'change.cause.import.unnamed': 'من استيراد ملف',
    'import.problem.header_missing': 'لا يحوي الملف العمود «{header}».',
    'import.problem.header_unknown': 'العمود «{header}» ليس في القالب.',
    'import.problem.header_twice': 'العمود «{header}» مكرّر.',
    'import.problem.required': '«{header}» فارغ.',
    'import.problem.integer': '«{header}» ليس عددًا صحيحًا: {value}',
    'import.problem.number': '«{header}» ليس رقمًا: {value}',
    'import.problem.date': '«{header}» ليس يومًا بصيغة {format}: {value}',
    'import.problem.date_ambiguous': '«{header}» يُقرأ يومًا أولًا أو شهرًا أولًا: {value}. اكتبه بصيغة 2021-04-03، أو اختر صيغة التاريخ.',
    'import.problem.date_unread': '«{header}» ليس يومًا: {value}',
    'list.import.date.auto': 'اكتشاف تلقائي',
    'list.import.guide.sheet': 'تعليمات',
    'list.import.guide.lede': 'املأ الورقة الأولى، صفاً لكل سجل تحت صف العناوين، واترك العناوين كما هي. هذه الورقة لا تُستورد.',
    'list.import.guide.column': 'العمود',
    'list.import.guide.required': 'مطلوب',
    'list.import.guide.what': 'ما يُكتب فيه',
    'list.import.guide.values': 'القيم المقبولة',
    'list.import.guide.example': 'مثال',
    'list.import.guide.requiredPrompt': 'مطلوب',
    'list.import.create': 'أضف «{value}»',
    'list.import.created': 'أُضيف «{value}». يُفحص الملف من جديد.',
    'unit.rows.zero': '{n} صف',
    'unit.rows.one': 'صف واحد',
    'unit.rows.two': 'صفّان',
    'unit.rows.few': '{n} صفوف',
    'unit.rows.many': '{n} صفًا',
    'unit.rows.other': '{n} صف',
    'unit.rows_in.zero': '{n} صف',
    'unit.rows_in.one': 'صف واحد',
    'unit.rows_in.two': 'صفّين',
    'unit.rows_in.few': '{n} صفوف',
    'unit.rows_in.many': '{n} صفًا',
    'unit.rows_in.other': '{n} صف',
    'unit.columns.zero': '{n} عمود',
    'unit.columns.one': 'عمود واحد',
    'unit.columns.two': 'عمودان',
    'unit.columns.few': '{n} أعمدة',
    'unit.columns.many': '{n} عمودًا',
    'unit.columns.other': '{n} عمود',
    'unit.columns_of.zero': '{n} عمود',
    'unit.columns_of.one': 'عمود واحد',
    'unit.columns_of.two': 'عمودين',
    'unit.columns_of.few': '{n} أعمدة',
    'unit.columns_of.many': '{n} عمودًا',
    'unit.columns_of.other': '{n} عمود',
    'unit.problems.zero': '{n} مشكلة',
    'unit.problems.one': 'مشكلة واحدة',
    'unit.problems.two': 'مشكلتين',
    'unit.problems.few': '{n} مشكلات',
    'unit.problems.many': '{n} مشكلةً',
    'unit.problems.other': '{n} مشكلة',
    'import.problem.boolean': '«{header}» ليس نعم أو لا: {value}',
    'import.problem.choice': '«{header}» ليس واحدًا من: {choices}',
    'import.problem.too_long': '«{header}» أطول من {max} حرفًا.',
    'import.problem.duplicate': 'السطر {first} يحمل «{header}» نفسه.',
    'import.problem.no_rows': 'لا صفوف في الملف تحت صف العناوين.',
    'import.problem.too_many': 'يحمل الملف {max:rows} على الأكثر.',
    'import.problem.no_file': 'لم يُرسل ملف.',
    'import.problem.empty': 'الملف فارغ.',
    'import.problem.too_large': 'الملف أكبر من المسموح.',
    'import.problem.format': 'يُقرأ الملف بصيغة ‎.xlsx أو ‎.csv: احفظه بإحداهما وارفعه.',
    'import.problem.already_applied': 'اعتُمد هذا الملف في {at}: اعتماده ثانية يكتب صفوفه مرتين.',
    'import.refusal.not_found': 'لا يوجد استيراد كهذا.',
    'import.refusal.applied': 'اعتُمد هذا الاستيراد من قبل.',
    'import.refusal.has_errors': 'ملف فيه صفوف مرفوضة لا يُعتمد منه شيء. افحصه من جديد لتعرف السبب.',
    'change.field.occasion.import_id': 'من استيراد ملف',
    'change.field.import_template.importer': 'المستورِد',
    'change.field.import_template.name': 'الاسم',
    'change.field.import_template.description': 'الوصف',
    'change.field.import_template.shared': 'مشترك',
    'change.field.import_template.settings': 'الإعدادات',
    'change.field.import_template.headers': 'عناوين الملف',
    'change.field.import_template.created_by': 'أنشأه',
};
//# sourceMappingURL=i18n-imports.js.map